/**
 * {@link ParticipantManagement} — invite participants by email, list them, and
 * delete participants who have no recorded scores.
 *
 * Requirements: 3.1, 3.9
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import { ConfirmDialog } from './ConfirmDialog.js';
import type { CSSProperties } from 'react';

export interface ParticipantItem {
  publicId: string;
  name: string;
  email?: string;
  teamName?: string | null;
}

export interface ParticipantManagementProps {
  eventId: string;
  apiClient: ApiClient;
}

const sectionStyle: CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '1rem',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const listItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 0',
  borderBottom: '1px solid var(--color-border, #E5E7EB)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  borderRadius: 'var(--radius-button, 8px)',
  border: '1px solid var(--color-border, #E5E7EB)',
};

export function ParticipantManagement({
  eventId,
  apiClient,
}: ParticipantManagementProps): JSX.Element {
  const [participants, setParticipants] = useState<ParticipantItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ParticipantItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchParticipants = useCallback(async () => {
    try {
      setLoading(true);
      const [pData, tData] = await Promise.all([
        apiClient.request<{
          participants: { id: string; name: string; contactInfo: string; teamId: string | null }[];
        }>('GET', `/events/${encodeURIComponent(eventId)}/participants`),
        apiClient.request<{ teams: { id: string; name: string }[] }>(
          'GET',
          `/events/${encodeURIComponent(eventId)}/teams`,
        ),
      ]);
      const teamNameById = new Map(tData.teams.map((t) => [t.id, t.name]));
      setParticipants(
        pData.participants.map((p) => ({
          publicId: p.id,
          name: p.name,
          email: p.contactInfo,
          teamName: p.teamId ? (teamNameById.get(p.teamId) ?? null) : null,
        })),
      );
      setError(null);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load participants.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, eventId]);

  useEffect(() => {
    void fetchParticipants();
  }, [fetchParticipants]);

  async function handleInvite(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;

    setInviting(true);
    setError(null);
    try {
      await apiClient.request<unknown>(
        'POST',
        `/events/${encodeURIComponent(eventId)}/invitations`,
        { body: { email } },
      );
      setInviteEmail('');
      await fetchParticipants();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to invite participant.');
    } finally {
      setInviting(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await apiClient.request<void>(
        'DELETE',
        `/events/${encodeURIComponent(eventId)}/participants/${encodeURIComponent(deleteTarget.publicId)}`,
      );
      setDeleteTarget(null);
      await fetchParticipants();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to delete participant.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section style={sectionStyle} aria-labelledby="participant-mgmt-heading">
      <h3 id="participant-mgmt-heading" style={headingStyle}>
        Participants
      </h3>

      {error && (
        <ErrorBanner title="Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {/* Invite form */}
      <form
        onSubmit={(e) => { void handleInvite(e); }}
        style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}
        noValidate
      >
        <label htmlFor="invite-email" style={{ display: 'none' }}>
          Participant email
        </label>
        <input
          id="invite-email"
          type="email"
          placeholder="Invite by email address"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          style={inputStyle}
          aria-label="Participant email address"
        />
        <Button type="submit" disabled={inviting || !inviteEmail.trim()}>
          {inviting ? <Spinner size="1rem" label="Inviting…" /> : 'Invite'}
        </Button>
      </form>

      {/* Participant list */}
      {loading ? (
        <Spinner label="Loading participants…" />
      ) : participants.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No participants yet.</p>
      ) : (
        <ul style={listStyle} aria-label="Participant list">
          {participants.map((p) => (
            <li key={p.publicId} style={listItemStyle}>
              <div>
                <span style={{ fontWeight: 500 }}>{p.name || p.publicId}</span>
                {p.teamName ? (
                  <span style={{ marginLeft: '0.5rem', color: 'var(--color-text-secondary, #6B7280)', fontSize: 'var(--font-size-body-sm, 0.875rem)' }}>
                    ({p.teamName})
                  </span>
                ) : (
                  <span style={{ marginLeft: '0.5rem', color: 'var(--color-text-secondary, #6B7280)', fontSize: 'var(--font-size-body-sm, 0.875rem)' }}>
                    (no team)
                  </span>
                )}
                {p.email && (
                  <span style={{ display: 'block', color: 'var(--color-text-secondary, #6B7280)', fontSize: 'var(--font-size-body-sm, 0.875rem)' }}>
                    {p.email}
                  </span>
                )}
              </div>
              <Button
                variant="secondary"
                onClick={() => setDeleteTarget(p)}
                aria-label={`Remove ${p.name || p.email || 'participant'}`}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Confirm delete dialog */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Remove Participant"
        message={`Are you sure you want to remove ${deleteTarget?.name || deleteTarget?.email || 'this participant'}? This action cannot be undone. Participants with recorded scores cannot be removed.`}
        confirmLabel={deleting ? 'Removing…' : 'Remove'}
        onConfirm={() => { void handleDelete(); }}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
}
